import React, { useState, useEffect } from 'react';
import { reportAPI } from '../lib/api';
import { ChevronLeft, ChevronRight, Target, TrendingUp, TrendingDown, Clock, Users, AlertTriangle, CheckCircle2, Coffee } from 'lucide-react';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
};

export default function ResourceUtilization() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => { fetchData(); }, [month]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await reportAPI.getResourceUtilization(month);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const changeMonth = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = (() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })();

  const resources = data?.resources || [];
  const target = data?.target_hours || 160;
  const totalActual = resources.reduce((s, r) => s + r.total_actual_hours, 0);
  const onTrack = resources.filter(r => r.status === 'on_track').length;
  const atRisk = resources.filter(r => r.status === 'at_risk').length;
  const under = resources.filter(r => r.status === 'under').length;
  const maxHours = Math.max(...resources.map(r => r.total_actual_hours), target, 1);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="resource-utilization-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-slate-900">Resource Utilization</h1>
            <p className="text-xs text-slate-400 mt-0.5">Monthly hours tracking vs {target}hr target</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => changeMonth(-1)} className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="ru-prev-month">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-slate-700 min-w-[140px] text-center" data-testid="ru-month-label">{monthLabel}</span>
            <button onClick={() => changeMonth(1)} className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="ru-next-month">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Hours Logged', value: `${totalActual.toFixed(0)}h`, sub: `${resources.length} resources`, icon: Clock, color: 'bg-blue-50 text-blue-600' },
            { label: 'On Track (≥90%)', value: onTrack, sub: 'Reaching target', icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-600' },
            { label: 'At Risk (60-90%)', value: atRisk, sub: 'Needs attention', icon: AlertTriangle, color: 'bg-amber-50 text-amber-600' },
            { label: 'Under-utilized (<60%)', value: under, sub: 'Below capacity', icon: Coffee, color: 'bg-red-50 text-red-500' },
          ].map((card, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`ru-card-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400">{card.label}</span>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.color}`}>
                  <card.icon size={16} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{card.value}</div>
              <span className="text-xs text-slate-400">{card.sub}</span>
            </div>
          ))}
        </div>

        {/* Resource table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <h2 className="text-sm font-semibold text-slate-700">Resource Breakdown</h2>
            <span className="text-xs text-slate-400">Target: {target}h/month</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : resources.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <Users size={32} className="text-slate-200" />
              <p className="text-sm text-slate-400">No logged hours for {monthLabel}</p>
              <p className="text-xs text-slate-300">Set actual_hours on tasks to see data here</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block divide-y divide-slate-50">
                {/* Header */}
                <div className="grid grid-cols-12 gap-3 px-5 py-2.5 bg-slate-50 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                  <div className="col-span-3">Resource</div>
                  <div className="col-span-1 text-right">Tasks</div>
                  <div className="col-span-1 text-right">Actual</div>
                  <div className="col-span-1 text-right">Est.</div>
                  <div className="col-span-1 text-right">Regular</div>
                  <div className="col-span-1 text-right">Support</div>
                  <div className="col-span-3">Utilization</div>
                  <div className="col-span-1 text-center">Status</div>
                </div>
                {resources.map((r, i) => {
                  const pct = Math.min(r.utilization_pct, 150);
                  const barColor = r.status === 'on_track' ? 'bg-emerald-400' : r.status === 'at_risk' ? 'bg-amber-400' : 'bg-red-400';
                  return (
                    <div key={r.id} className="grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-slate-50/50 transition-colors" data-testid={`ru-row-${i}`}>
                      <div className="col-span-3 flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-[#002FA7]">{getInitials(r.name)}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-slate-700 block">{r.name}</span>
                          <span className="text-[10px] text-slate-400">{r.role}</span>
                        </div>
                      </div>
                      <div className="col-span-1 text-right">
                        <span className="text-sm text-slate-600">{r.task_count}</span>
                        {r.subtask_count > 0 && <span className="text-[10px] text-slate-400 block">+{r.subtask_count} sub</span>}
                      </div>
                      <div className="col-span-1 text-right text-sm font-semibold text-slate-800">{r.total_actual_hours}h</div>
                      <div className="col-span-1 text-right text-sm text-slate-500">{r.total_estimated_hours}h</div>
                      <div className="col-span-1 text-right text-sm text-blue-600">{r.regular_hours}h</div>
                      <div className="col-span-1 text-right text-sm text-purple-600">{r.support_hours}h</div>
                      <div className="col-span-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden relative">
                            <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${Math.min(pct / 1.5, 100)}%` }} />
                            <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400" style={{ left: `${(100 / 1.5)}%` }} title={`Target: ${target}h`} />
                          </div>
                          <span className="text-xs font-semibold text-slate-600 w-10 text-right">{r.utilization_pct}%</span>
                        </div>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          r.status === 'on_track' ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' :
                          r.status === 'at_risk' ? 'bg-amber-50 text-amber-600 border border-amber-200' :
                          'bg-red-50 text-red-500 border border-red-200'
                        }`}>
                          {r.status === 'on_track' ? 'ON TRACK' : r.status === 'at_risk' ? 'AT RISK' : 'UNDER'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile Card Layout */}
              <div className="md:hidden divide-y divide-slate-100">
                {resources.map((r, i) => {
                  const pct = Math.min(r.utilization_pct, 150);
                  const barColor = r.status === 'on_track' ? 'bg-emerald-400' : r.status === 'at_risk' ? 'bg-amber-400' : 'bg-red-400';
                  const statusLabel = r.status === 'on_track' ? 'ON TRACK' : r.status === 'at_risk' ? 'AT RISK' : 'UNDER';
                  const statusCls = r.status === 'on_track' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                    r.status === 'at_risk' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                    'bg-red-50 text-red-500 border-red-200';
                  return (
                    <div key={r.id} className="p-4" data-testid={`ru-card-mobile-${i}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] font-bold text-[#002FA7]">{getInitials(r.name)}</span>
                          </div>
                          <div className="min-w-0">
                            <span className="text-sm font-semibold text-slate-800 block truncate">{r.name}</span>
                            <span className="text-[10px] text-slate-400">{r.role}</span>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${statusCls}`}>
                          {statusLabel}
                        </span>
                      </div>
                      {/* Utilization bar */}
                      <div className="mt-3 flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden relative">
                          <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${Math.min(pct / 1.5, 100)}%` }} />
                          <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400" style={{ left: `${(100 / 1.5)}%` }} />
                        </div>
                        <span className="text-xs font-bold text-slate-700 w-10 text-right">{r.utilization_pct}%</span>
                      </div>
                      {/* Stats row */}
                      <div className="mt-2.5 flex items-center gap-3 text-[11px] flex-wrap">
                        <span className="text-slate-500"><span className="font-semibold text-slate-800">{r.total_actual_hours}h</span> actual</span>
                        <span className="text-slate-400">·</span>
                        <span className="text-slate-500"><span className="font-medium">{r.total_estimated_hours}h</span> est</span>
                        <span className="text-slate-400">·</span>
                        <span className="text-slate-500">{r.task_count} tasks{r.subtask_count > 0 ? ` +${r.subtask_count} sub` : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
